/**
 * CartNotification
 */
class CartNotification extends HTMLElement {
  connectedCallback() {
    // Elements
    this.cartNotificationOverlay = this.querySelector('[data-cart-notification="overlay"]');
    this.validContent = this.querySelector('[data-cart-notification-valid]');
    this.invalidContent = this.querySelector('[data-cart-notification-invalid]');

    // Events
    this.closeClickOutside = (event) => {
      if (!this.querySelector('[data-cart-notification="container"]').contains(event.target)) {
        this.close();
      }
    };
    this.cartNotificationOverlay.addEventListener('keyup', (evt) => {
      evt.code === 'Escape' && this.close();
    });
    this.querySelectorAll('[data-cart-notification-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.close());
    });
  }

  /**
   * toggleNotificationClasses
   * @description Toggles the right classes to show or hide the cart notification
   */
  toggleOpenState(state = true) {
    state ? this.classList.add('open') : this.classList.remove('open');
    state ? this.setAttribute('aria-hidden', 'false') : this.setAttribute('aria-hidden', 'true');
  }

  /**
   * open
   * @param {Boolean} hasError
   */
  open(hasError = false) {
    if (hasError) {
      this.validContent.classList.add('hidden');
      this.invalidContent.classList.remove('hidden');
    } else {
      this.validContent.classList.remove('hidden');
      this.invalidContent.classList.add('hidden');
    }

    this.toggleOpenState();

    this.cartNotificationOverlay?.addEventListener(
      'transitionend',
      () => {
        this.cartNotificationOverlay.focus();
        trapFocus(this.cartNotificationOverlay);
        document.body.addEventListener('click', this.closeClickOutside);
      },
      { once: true }
    );
  }

  /**
   * close
   */
  close() {
    this.toggleOpenState(false);
    document.body.removeEventListener('click', this.closeClickOutside);
    removeTrapFocus(this.activeElement);
    this?.unmountRecommendations?.();
  }

  getComplimentaryProducts(productId) {
    const config = {
      id: 'react-root-cart-notification-related',
      productId: productId,
      title: this.getAttribute('data-recommendations-title') ?? '',
      layout: 'list',
      count: 3,
    };
    const sectionId = this.getAttribute('data-section-id');

    const render = () => {
      if (!window?.loop54ComplimentaryProducts?.render) {
        this.checkForComplimentaryProducts = requestAnimationFrame(render);

        return;
      }

      window.loop54ComplimentaryProducts.render(config);
    };

    this.unmountRecommendations = () => {
      window.cancelAnimationFrame(this.checkForComplimentaryProducts);
      window?.loop54ComplimentaryProducts?.unmount?.();
    };

    if (Shopify.designMode && !this.setDesignModeListeners) {
      this.setDesignModeListeners = true;

      window.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === sectionId) {
          render();
        }
      });

      window.addEventListener('shopify:section:unload', (event) => {
        if (event.detail.sectionId === sectionId) {
          this.unmountRecommendations();
        }
      });
    }

    render();
  }

  /**
   * renderContents
   * @param {Object} parsedState
   */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.getComplimentaryProducts(parsedState.rootProductId);
    this.open();
  }

  /**
   * renderQuantityError
   * @param {String} inventoryQuantity
   */
  renderQuantityError(inventoryQuantity) {
    const errorMessageElem = this.cartNotificationOverlay.querySelector('[data-cart-notification-error-message]');
    const inventoryQty = parseInt(inventoryQuantity) || 0;

    errorMessageElem.innerText = window.cartStrings.quantityError.replace('[quantity]', inventoryQty);

    this.open(true);
  }

  /**
   * getSectionsToRender
   * @returns {Array} Sections to render
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `#cart-notification-product-${this.productId}`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
    ];
  }

  /**
   * getSectionInnerHTML
   * @param {String} String that contains html
   * @param {String} selector
   * @returns
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  /**
   * setActiveElement
   * @param {Node} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  disconnectedCallback() {
    this.unmountRecommendations?.();
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
